import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { ImageUploadField } from '../../components/image-upload-field';
import { toDateInputValue } from '../../../../utils/date';
import { TIPO_BANNER_LABEL, TipoBanner, enumOptions } from '../../../../model/apm/apm-enums';
import { IBannerAddEditRequest } from '../../../../model/apm/banner/request/banner-add-edit-request';
import { IBannerResponse } from '../../../../model/apm/banner/response/banner-response';

interface BannerDrawerProps {
    open: boolean;
    initial?: IBannerResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IBannerAddEditRequest) => void;
}

const emptyValue: IBannerAddEditRequest = { type: TipoBanner.Integrado, isPermanent: true, showOnSep: false };

export function BannerDrawer({ open, initial, isSaving, onClose, onSave }: BannerDrawerProps) {
    const [form, setForm] = useState<IBannerAddEditRequest>(emptyValue);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const isEdit = !!initial;
    const periodInvalid = !form.isPermanent && !!form.startDate && !!form.endDate && form.endDate < form.startDate;
    const errors = {
        title: showValidationErrors && !form.title?.trim(),
        image: showValidationErrors && !form.image,
        startDate: showValidationErrors && !form.isPermanent && !form.startDate,
        endDate: showValidationErrors && !form.isPermanent && (!form.endDate || periodInvalid),
    };

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                title: initial.title,
                url: initial.url,
                image: initial.image,
                type: initial.type,
                isPermanent: initial.isPermanent,
                startDate: toDateInputValue(initial.startDate),
                endDate: toDateInputValue(initial.endDate),
                showOnSep: initial.showOnSep,
            }
            : { ...emptyValue });
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IBannerAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        const missingPeriod = !form.isPermanent && (!form.startDate || !form.endDate || periodInvalid);
        if (!form.title?.trim() || !form.image || missingPeriod) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            title: form.title.trim(),
            url: form.url?.trim() || undefined,
            // Banner permanente não tem período; a API também descarta as datas
            startDate: form.isPermanent ? undefined : form.startDate,
            endDate: form.isPermanent ? undefined : form.endDate,
        });
    }

    return (
        <FormDrawer
            open={open}
            icon={<ViewCarouselIcon />}
            title={isEdit ? 'Editar banner' : 'Novo banner'}
            subtitle={isEdit ? initial?.title : 'O banner é criado como rascunho; publique-o pela listagem'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar banner'}
            isSaving={isSaving}
            saveDisabled={isUploadingImage}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Título</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            inputProps={{ maxLength: 150 }}
                            value={form.title || ''}
                            onChange={(e) => update({ title: e.target.value })}
                            error={errors.title}
                        />
                        {errors.title
                            ? <FormHelperText error>Título é obrigatório</FormHelperText>
                            : <FormHelperText>Usado como texto alternativo da imagem no portal</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Imagem</InputLabel>
                        <ImageUploadField
                            id="banner-image"
                            value={form.image}
                            onChange={(image) => update({ image })}
                            onUploadingChange={setIsUploadingImage}
                            error={errors.image}
                            helperText={errors.image ? 'Imagem é obrigatória' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Link</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            placeholder="https://"
                            inputProps={{ maxLength: 250 }}
                            value={form.url || ''}
                            onChange={(e) => update({ url: e.target.value })}
                        />
                        <FormHelperText>Opcional: endereço aberto ao clicar no banner</FormHelperText>
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Exibição">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Onde exibir</InputLabel>
                        <Select fullWidth value={form.type} onChange={(e) => update({ type: Number(e.target.value) })}>
                            {enumOptions(TIPO_BANNER_LABEL).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                        <FormHelperText>Carrossel no topo do portal e/ou popup ao abrir o portal</FormHelperText>
                    </Grid>
                    <Grid item xs={12}>
                        <SwitchField
                            checked={form.isPermanent}
                            onChange={(isPermanent) => update({ isPermanent })}
                            label="Exibição permanente"
                            description="Desligue para exibir o banner somente durante um período."
                        />
                    </Grid>
                    {!form.isPermanent && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <InputLabel required>Exibir de</InputLabel>
                                <OutlinedInput fullWidth type="date" value={form.startDate || ''} onChange={(e) => update({ startDate: e.target.value || undefined })} error={errors.startDate} />
                                {errors.startDate && <FormHelperText error>Data inicial é obrigatória</FormHelperText>}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel required>Até</InputLabel>
                                <OutlinedInput
                                    fullWidth
                                    type="date"
                                    inputProps={{ min: form.startDate }}
                                    value={form.endDate || ''}
                                    onChange={(e) => update({ endDate: e.target.value || undefined })}
                                    error={errors.endDate}
                                />
                                {errors.endDate && (
                                    <FormHelperText error>{periodInvalid ? 'A data final deve ser igual ou posterior à inicial' : 'Data final é obrigatória'}</FormHelperText>
                                )}
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <SwitchField
                            checked={form.showOnSep}
                            onChange={(showOnSep) => update({ showOnSep })}
                            label="Exibir no SEP"
                            description="Também mostra o banner no SEP da entidade."
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
